import "./styles.css";
import { ARB_FILENAMES, extractPlaceholders, parseArb, validateArbSet } from "./arb";
import { directoryWriter, openWorkspaceFolder, writableDirectorySupportError } from "./filesystem";
import { extractMetadataFromArb } from "./metadata";
import { writeFilesWithRollback } from "./save";
import { EditorStore } from "./store";
import { LOCALES, STATUSES, type Locale, type OpenedWorkspace, type TranslationStatus } from "./types";
import { createZip } from "./zip";

const LOCALE_LABELS: Record<Locale, string> = { de: "German", en: "English", fr: "French", it: "Italian" };

interface FilterState {
  search: string;
  locale: Locale | "all";
  status: TranslationStatus | "all";
  issue: "all" | "empty" | "over-limit" | "placeholders" | "orphans";
  tag: string;
}

const filters: FilterState = { search: "", locale: "all", status: "all", issue: "all", tag: "" };
let store: EditorStore | null = null;
let displayLimit = 300;
let renderTimer: number | undefined;
let demoMode = false;

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Application root is missing.");

app.innerHTML = `
  <div class="app-shell">
    <header class="topbar">
      <div class="brand-block">
        <img class="brand-logo" src="/brand/wb-logo.svg" width="530" height="259" alt="Working Bicycle" />
        <div class="product-name">
          <p class="eyebrow">Content operations / four locales</p>
          <h1>Localization workbench</h1>
        </div>
      </div>
      <div class="topbar-actions">
        <div class="local-only" title="The editor makes no remote requests">
          <span class="pulse-dot" aria-hidden="true"></span> Local only
        </div>
        <button class="button button-secondary" id="open-folder" type="button">
          <span aria-hidden="true">↗</span> Open data folder
        </button>
        <button class="button button-secondary" id="download-arbs" type="button" disabled title="Download all four current ARBs as one ZIP">
          <span aria-hidden="true">↓</span> Download ZIP
        </button>
        <button class="button button-primary" id="save-changes" type="button" disabled>
          Save changes
        </button>
      </div>
    </header>

    <div class="save-state" id="save-state" aria-live="polite">
      <span class="save-state-dot"></span><span>No folder open</span>
    </div>

    <section class="summary-strip" id="summary-strip" aria-label="Localization summary">
      <div class="summary-intro"><span>Workspace pulse</span><strong>Open the repository’s data folder to begin.</strong></div>
    </section>

    <main class="workspace" id="workspace" hidden>
      <aside class="key-panel" aria-label="Translation keys">
        <div class="panel-heading">
          <div><p class="eyebrow">Inventory</p><h2>Translation keys</h2></div>
          <span class="result-count" id="result-count">0</span>
        </div>
        <label class="search-field">
          <span class="visually-hidden">Search translation keys</span>
          <span aria-hidden="true">⌕</span>
          <input id="search" type="search" placeholder="Search key, copy, note…" autocomplete="off" />
          <kbd>Ctrl K</kbd>
        </label>
        <div class="filter-grid">
          <label>
            <span>Locale</span>
            <select id="locale-filter">
              <option value="all">All locales</option>
              <option value="de">DE · German</option>
              <option value="en">EN · English</option>
              <option value="fr">FR · French</option>
              <option value="it">IT · Italian</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select id="status-filter">
              <option value="all">Any status</option>
              <option value="new">New</option>
              <option value="draft">Draft</option>
              <option value="review">In review</option>
              <option value="final">Final</option>
            </select>
          </label>
          <label>
            <span>Attention</span>
            <select id="issue-filter">
              <option value="all">Everything</option>
              <option value="empty">Empty copy</option>
              <option value="over-limit">Over character limit</option>
              <option value="placeholders">Uses placeholders</option>
              <option value="orphans">Orphaned metadata</option>
            </select>
          </label>
          <label>
            <span>Tag</span>
            <select id="tag-filter"><option value="">All tags</option></select>
          </label>
        </div>
        <div class="locale-legend" aria-label="Status rail legend">
          ${LOCALES.map((locale) => `<span><i class="locale-chip locale-${locale}">${locale}</i>${LOCALE_LABELS[locale]}</span>`).join("")}
        </div>
        <div class="key-list" id="key-list"></div>
        <button class="load-more" id="load-more" type="button" hidden>Show more keys</button>
        <div class="orphan-list" id="orphan-list"></div>
      </aside>

      <section class="editor-panel" id="editor-panel" aria-label="Translation editor"></section>
    </main>

    <section class="welcome-state" id="welcome-state">
      <div class="welcome-index" aria-hidden="true">
        <span class="locale-de">DE</span><span class="locale-en">EN</span><span class="locale-fr">FR</span><span class="locale-it">IT</span>
      </div>
      <p class="eyebrow">One key. Four clear voices.</p>
      <h2>Open the <code>data</code> folder.</h2>
      <p>The workbench validates every ARB before editing and writes only the files you change. Your content never leaves this computer.</p>
      <button class="button button-primary" id="welcome-open" type="button">Open data folder</button>
      <p class="browser-note" id="browser-note"></p>
    </section>

    <div class="toast" id="toast" role="status" aria-live="polite"></div>
  </div>
`;

function element<T extends Element>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing UI element: ${selector}`);
  return found;
}

const openButton = element<HTMLButtonElement>("#open-folder");
const welcomeOpenButton = element<HTMLButtonElement>("#welcome-open");
const downloadButton = element<HTMLButtonElement>("#download-arbs");
const saveButton = element<HTMLButtonElement>("#save-changes");
const workspaceElement = element<HTMLElement>("#workspace");
const welcomeElement = element<HTMLElement>("#welcome-state");
const editorElement = element<HTMLElement>("#editor-panel");
const keyListElement = element<HTMLElement>("#key-list");
const orphanListElement = element<HTMLElement>("#orphan-list");
const loadMoreButton = element<HTMLButtonElement>("#load-more");
const toastElement = element<HTMLElement>("#toast");

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  })[character] ?? character);
}

function showToast(message: string, kind: "success" | "error" | "info" = "info"): void {
  toastElement.textContent = message;
  toastElement.className = `toast toast-${kind} toast-visible`;
  window.setTimeout(() => toastElement.classList.remove("toast-visible"), kind === "error" ? 7000 : 3500);
}

function setSaveState(): void {
  const state = element<HTMLElement>("#save-state");
  if (!store) {
    state.innerHTML = `<span class="save-state-dot"></span><span>No folder open</span>`;
    state.className = "save-state";
    saveButton.disabled = true;
    saveButton.textContent = "Save changes";
    return;
  }
  const errors = store.issues().filter((issue) => issue.level === "error");
  if (errors.length) {
    state.innerHTML = `<span class="save-state-dot"></span><span>${errors.length} structural error${errors.length === 1 ? "" : "s"} block saving</span>`;
    state.className = "save-state save-state-error";
    saveButton.disabled = true;
    saveButton.textContent = "Save changes";
  } else if (store.isDirty) {
    state.innerHTML = `<span class="save-state-dot"></span><span>Unsaved local changes</span>`;
    state.className = "save-state save-state-dirty";
    saveButton.disabled = false;
    saveButton.textContent = "Save changes";
  } else {
    state.innerHTML = `<span class="save-state-dot"></span><span>All changes saved locally</span>`;
    state.className = "save-state save-state-clean";
    saveButton.disabled = true;
    saveButton.textContent = "Save changes";
  }
}

function renderSummary(): void {
  const summary = element<HTMLElement>("#summary-strip");
  if (!store) return;
  const counts = store.counts();
  const finalByLocale = LOCALES.map((locale) => `
    <div class="summary-locale">
      <span class="locale-chip locale-${locale}">${locale}</span>
      <strong>${counts.statuses[locale].final}</strong>
      <small>final</small>
    </div>
  `).join("");
  summary.innerHTML = `
    <div class="summary-intro"><span>Workspace pulse</span><strong>${counts.keys.toLocaleString()} translation keys</strong></div>
    <div class="summary-metric"><strong>${counts.placeholderKeys}</strong><span>with variables</span></div>
    <div class="summary-metric ${counts.empty ? "metric-warning" : ""}"><strong>${counts.empty}</strong><span>empty values</span></div>
    <div class="summary-metric ${counts.overLimit ? "metric-warning" : ""}"><strong>${counts.overLimit}</strong><span>over limit</span></div>
    <div class="summary-metric ${counts.orphanMetadata ? "metric-warning" : ""}"><strong>${counts.orphanMetadata}</strong><span>orphaned</span></div>
    <div class="summary-language-group">${finalByLocale}</div>
  `;
}

function allTags(): string[] {
  if (!store) return [];
  const tags = new Map<string, string>();
  for (const entry of Object.values(store.workspace.metadata.entries)) {
    for (const tag of entry.tags ?? []) tags.set(tag.toLocaleLowerCase(), tag);
  }
  return [...tags.values()].sort((left, right) => left.localeCompare(right));
}

function renderTagFilter(): void {
  const select = element<HTMLSelectElement>("#tag-filter");
  const options = allTags().map((tag) => `<option value="${escapeHtml(tag)}">${escapeHtml(tag)}</option>`).join("");
  select.innerHTML = `<option value="">All tags</option>${options}`;
  select.value = filters.tag;
}

function keyMatches(key: string): boolean {
  if (!store) return false;
  const metadata = store.getMetadata(key);
  const searchHaystack = [
    key,
    ...LOCALES.map((locale) => store?.getTranslation(key, locale) ?? ""),
    metadata.description ?? "",
    metadata.note ?? "",
    ...(metadata.tags ?? []),
  ].join("\n").toLocaleLowerCase();
  if (filters.search && !searchHaystack.includes(filters.search.toLocaleLowerCase())) return false;

  const locales = filters.locale === "all" ? LOCALES : [filters.locale];
  if (filters.status !== "all" && !locales.some((locale) => store?.getStatus(key, locale) === filters.status)) return false;
  if (filters.tag && !(metadata.tags ?? []).some((tag) => tag.toLocaleLowerCase() === filters.tag.toLocaleLowerCase())) return false;

  if (filters.issue === "empty" && !locales.some((locale) => !store?.getTranslation(key, locale).trim())) return false;
  if (filters.issue === "over-limit") {
    const limit = metadata.characterLimit ?? null;
    if (limit === null || !locales.some((locale) => [...(store?.getTranslation(key, locale) ?? "")].length > limit)) return false;
  }
  if (filters.issue === "placeholders" && !locales.some((locale) => extractPlaceholders(store?.getTranslation(key, locale) ?? "").length)) return false;
  return filters.issue !== "orphans";
}

function renderList(): void {
  if (!store) return;
  const filtered = store.keys.filter(keyMatches);
  const visible = filtered.slice(0, displayLimit);
  element<HTMLElement>("#result-count").textContent = filtered.length.toLocaleString();
  keyListElement.innerHTML = visible.length
    ? visible.map((key) => {
        const active = key === store?.selectedKey;
        const german = store?.getTranslation(key, "de") ?? "";
        const hasEmpty = LOCALES.some((locale) => !store?.getTranslation(key, locale).trim());
        const rail = LOCALES.map((locale) => {
          const status = store?.getStatus(key, locale) ?? "new";
          return `<i class="status-tick locale-${locale} status-${status}" title="${locale.toUpperCase()}: ${status}"></i>`;
        }).join("");
        return `
          <button class="key-row${active ? " key-row-active" : ""}" type="button" data-key="${escapeHtml(key)}">
            <span class="status-rail" aria-hidden="true">${rail}</span>
            <span class="key-copy"><code>${escapeHtml(key)}</code><small>${escapeHtml(german || "Empty German translation")}</small></span>
            ${hasEmpty ? `<span class="row-warning" title="Contains empty copy">!</span>` : ""}
          </button>
        `;
      }).join("")
    : `<div class="list-empty"><strong>No keys match.</strong><span>Change the filters or search phrase.</span></div>`;
  loadMoreButton.hidden = visible.length >= filtered.length;
  loadMoreButton.textContent = `Show ${Math.min(300, filtered.length - visible.length)} more`;
  renderOrphans();
}

function renderOrphans(): void {
  if (!store) return;
  const orphans = store.getOrphanMetadataKeys().filter((key) => !filters.search || key.toLocaleLowerCase().includes(filters.search.toLocaleLowerCase()));
  if (!orphans.length || !["all", "orphans"].includes(filters.issue)) {
    orphanListElement.innerHTML = filters.issue === "orphans" ? `<div class="list-empty"><strong>No orphaned metadata.</strong></div>` : "";
    return;
  }
  orphanListElement.innerHTML = `
    <div class="orphan-heading"><span>Orphaned metadata</span><strong>${orphans.length}</strong></div>
    ${orphans.map((key) => `
      <div class="orphan-row"><code>${escapeHtml(key)}</code><button type="button" data-remove-orphan="${escapeHtml(key)}">Remove</button></div>
    `).join("")}
  `;
}

function statusOptions(current: TranslationStatus): string {
  const labels: Record<TranslationStatus, string> = { new: "New", draft: "Draft", review: "In review", final: "Final" };
  return STATUSES.map((status) => `<option value="${status}"${status === current ? " selected" : ""}>${labels[status]}</option>`).join("");
}

function renderEditor(): void {
  if (!store || !store.selectedKey) {
    editorElement.innerHTML = `<div class="editor-empty"><strong>Select a translation key.</strong></div>`;
    return;
  }
  const key = store.selectedKey;
  const metadata = store.getMetadata(key);
  const placeholderUnion = [...new Set(LOCALES.flatMap((locale) => extractPlaceholders(store?.getTranslation(key, locale) ?? "")))].sort();

  editorElement.innerHTML = `
    <div class="editor-heading">
      <div>
        <p class="eyebrow">Selected key</p>
        <h2><code>${escapeHtml(key)}</code></h2>
      </div>
      <div class="placeholder-group" id="placeholder-group">
        ${placeholderUnion.length ? placeholderUnion.map((name) => `<span>{${escapeHtml(name)}}</span>`).join("") : `<span class="placeholder-none">No variables</span>`}
      </div>
    </div>
    <div class="structural-banner" id="structural-banner" hidden></div>

    <section class="metadata-card">
      <div class="section-title"><div><p class="eyebrow">Editorial context</p><h3>Guide every translation</h3></div><span>Shared across locales</span></div>
      <div class="metadata-grid">
        <label class="field field-wide"><span>Description / context</span><textarea id="meta-description" rows="2" placeholder="Where and how does this text appear?">${escapeHtml(metadata.description ?? "")}</textarea></label>
        <label class="field field-wide"><span>Internal note</span><textarea id="meta-note" rows="2" placeholder="Writing guidance, decisions, or follow-up…">${escapeHtml(metadata.note ?? "")}</textarea></label>
        <label class="field"><span>Tags <small>comma separated</small></span><input id="meta-tags" value="${escapeHtml((metadata.tags ?? []).join(", "))}" placeholder="onboarding, campaign" /></label>
        <label class="field"><span>Character limit</span><input id="meta-limit" type="number" min="1" step="1" value="${metadata.characterLimit ?? ""}" placeholder="No limit" /></label>
      </div>
    </section>

    <section class="translations-section">
      <div class="section-title"><div><p class="eyebrow">Translations</p><h3>Four language lanes</h3></div><span>Variables must match</span></div>
      <div class="translation-grid">
        ${LOCALES.map((locale) => {
          const value = store?.getTranslation(key, locale) ?? "";
          const limit = metadata.characterLimit ?? null;
          const count = [...value].length;
          const over = limit !== null && count > limit;
          return `
            <article class="translation-card locale-border-${locale}${!value.trim() ? " translation-empty" : ""}${over ? " translation-over" : ""}" data-locale-card="${locale}">
              <header>
                <div><span class="locale-chip locale-${locale}">${locale}</span><strong>${LOCALE_LABELS[locale]}</strong>${locale === "de" ? `<small>App default</small>` : locale === "en" ? `<small>ARB template</small>` : ""}</div>
                <select data-status="${locale}" aria-label="${LOCALE_LABELS[locale]} status">${statusOptions(store?.getStatus(key, locale) ?? "new")}</select>
              </header>
              <textarea data-translation="${locale}" rows="5" spellcheck="true" aria-label="${LOCALE_LABELS[locale]} translation">${escapeHtml(value)}</textarea>
              <footer>
                <span class="locale-placeholders" data-placeholders="${locale}">${extractPlaceholders(value).map((name) => `{${name}}`).join(" · ") || "No variables"}</span>
                <span class="character-count${over ? " count-over" : ""}" data-count="${locale}">${count}${limit !== null ? ` / ${limit}` : ""}</span>
              </footer>
            </article>
          `;
        }).join("")}
      </div>
    </section>
  `;
  bindEditorInputs();
  refreshEditorDiagnostics();
}

function bindEditorInputs(): void {
  if (!store) return;
  const key = store.selectedKey;
  editorElement.querySelectorAll<HTMLTextAreaElement>("[data-translation]").forEach((textarea) => {
    textarea.addEventListener("input", () => {
      const locale = textarea.dataset.translation as Locale;
      store?.updateTranslation(key, locale, textarea.value);
      refreshEditorDiagnostics();
      scheduleChromeRender();
    });
  });
  editorElement.querySelectorAll<HTMLSelectElement>("[data-status]").forEach((select) => {
    select.addEventListener("change", () => {
      store?.updateStatus(key, select.dataset.status as Locale, select.value as TranslationStatus);
      scheduleChromeRender();
    });
  });
  ["#meta-description", "#meta-note", "#meta-tags", "#meta-limit"].forEach((selector) => {
    elementWithin<HTMLInputElement | HTMLTextAreaElement>(editorElement, selector).addEventListener("input", () => {
      const limitInput = elementWithin<HTMLInputElement>(editorElement, "#meta-limit");
      const rawLimit = limitInput.value.trim();
      const parsedLimit = rawLimit ? Number(rawLimit) : null;
      const validLimit = parsedLimit === null || (Number.isInteger(parsedLimit) && parsedLimit > 0);
      limitInput.setCustomValidity(validLimit ? "" : "Use a positive whole number or leave this empty.");
      if (!validLimit) return;
      store?.updateSharedMetadata(key, {
        description: elementWithin<HTMLTextAreaElement>(editorElement, "#meta-description").value,
        note: elementWithin<HTMLTextAreaElement>(editorElement, "#meta-note").value,
        tags: elementWithin<HTMLInputElement>(editorElement, "#meta-tags").value.split(","),
        characterLimit: parsedLimit,
      });
      refreshEditorDiagnostics();
      scheduleChromeRender();
    });
  });
}

function elementWithin<T extends Element>(root: Element, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Missing editor element: ${selector}`);
  return found;
}

function refreshEditorDiagnostics(): void {
  if (!store) return;
  const key = store.selectedKey;
  const metadata = store.getMetadata(key);
  const limit = metadata.characterLimit ?? null;
  const expected = extractPlaceholders(store.getTranslation(key, "de"));
  const mismatches: Locale[] = [];
  for (const locale of LOCALES) {
    const value = store.getTranslation(key, locale);
    const count = [...value].length;
    const over = limit !== null && count > limit;
    const placeholders = extractPlaceholders(value);
    const countElement = editorElement.querySelector<HTMLElement>(`[data-count="${locale}"]`);
    const placeholderElement = editorElement.querySelector<HTMLElement>(`[data-placeholders="${locale}"]`);
    const card = editorElement.querySelector<HTMLElement>(`[data-locale-card="${locale}"]`);
    if (countElement) {
      countElement.textContent = `${count}${limit !== null ? ` / ${limit}` : ""}`;
      countElement.classList.toggle("count-over", over);
    }
    if (placeholderElement) placeholderElement.textContent = placeholders.map((name) => `{${name}}`).join(" · ") || "No variables";
    card?.classList.toggle("translation-over", over);
    card?.classList.toggle("translation-empty", !value.trim());
    if (placeholders.join("|") !== expected.join("|")) mismatches.push(locale);
  }
  const banner = elementWithin<HTMLElement>(editorElement, "#structural-banner");
  banner.hidden = mismatches.length === 0;
  banner.textContent = mismatches.length
    ? `Variable mismatch: ${mismatches.map((locale) => locale.toUpperCase()).join(", ")} must use the same variables as German (${expected.map((name) => `{${name}}`).join(", ") || "none"}).`
    : "";
}

function scheduleChromeRender(): void {
  window.clearTimeout(renderTimer);
  setSaveState();
  renderTimer = window.setTimeout(() => {
    renderSummary();
    renderTagFilter();
    renderList();
  }, 120);
}

async function openFolder(): Promise<void> {
  if (store?.isDirty && !window.confirm("Discard unsaved changes and open another folder?")) return;
  try {
    const workspace = await openWorkspaceFolder();
    finishOpeningWorkspace(workspace);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    showToast(error instanceof Error ? error.message : String(error), "error");
  }
}

function finishOpeningWorkspace(workspace: OpenedWorkspace): void {
  demoMode = false;
  loadWorkspace(workspace);
  if (workspace.normalizedLocales?.length) {
    showToast(
      `Opened ${store?.keys.length.toLocaleString()} keys and cleaned legacy metadata in ${workspace.normalizedLocales.length} ARBs. Save once to persist it.`,
      "info",
    );
  } else {
    showToast(`Opened ${store?.keys.length.toLocaleString()} translation keys.`, "success");
  }
}

function loadWorkspace(workspace: OpenedWorkspace): void {
  store = new EditorStore(workspace);
  downloadButton.disabled = false;
  workspaceElement.hidden = false;
  welcomeElement.hidden = true;
  filters.search = "";
  filters.locale = "all";
  filters.status = "all";
  filters.issue = "all";
  filters.tag = "";
  displayLimit = 300;
  element<HTMLInputElement>("#search").value = "";
  element<HTMLSelectElement>("#locale-filter").value = "all";
  element<HTMLSelectElement>("#status-filter").value = "all";
  element<HTMLSelectElement>("#issue-filter").value = "all";
  renderSummary();
  renderTagFilter();
  renderList();
  renderEditor();
  setSaveState();
}

function downloadArbs(): void {
  if (!store) return;
  const errors = store.issues().filter((issue) => issue.level === "error");
  if (errors.length) {
    showToast(errors[0]?.message ?? "Structural errors block exporting.", "error");
    return;
  }

  const files = store.exportFiles();
  const archive = createZip(files);
  const blob = new Blob([archive.buffer as ArrayBuffer], { type: "application/zip" });
  const objectUrl = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `wb-localizations-${date}.zip`;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  showToast(
    store.isDirty
      ? "Downloaded 4 current ARBs, including unsaved edits."
      : "Downloaded all 4 ARBs.",
    "success",
  );
}

async function saveChanges(): Promise<void> {
  if (!store || !store.isDirty) return;
  const errors = store.issues().filter((issue) => issue.level === "error");
  if (errors.length) {
    showToast(errors[0]?.message ?? "Structural errors block saving.", "error");
    return;
  }
  const files = store.dirtyFiles();
  saveButton.disabled = true;
  saveButton.textContent = "Saving…";
  try {
    if (demoMode) {
      localStorage.setItem("wb-localization-demo", JSON.stringify(Object.fromEntries(files.map((file) => [file.filename, file.content]))));
    } else {
      if (!store.workspace.directoryHandle) throw new Error("The writable folder permission is no longer available.");
      await writeFilesWithRollback(files, directoryWriter(store.workspace.directoryHandle));
    }
    store.markSaved(files);
    setSaveState();
    renderList();
    showToast(`Saved ${files.length} file${files.length === 1 ? "" : "s"} locally.`, "success");
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), "error");
    setSaveState();
  } finally {
    saveButton.textContent = "Save changes";
  }
}

async function loadDemo(): Promise<void> {
  demoMode = true;
  const stored = localStorage.getItem("wb-localization-demo");
  const saved = stored ? JSON.parse(stored) as Record<string, string> : {};
  const textFor = async (filename: string): Promise<string> => saved[filename] ?? (await fetch(`/data/${filename}`)).text();
  const arbPairs = await Promise.all(LOCALES.map(async (locale) => {
    const filename = ARB_FILENAMES[locale];
    return [locale, parseArb(filename, await textFor(filename), locale)] as const;
  }));
  const arbs = Object.fromEntries(arbPairs) as OpenedWorkspace["arbs"];
  const issues = validateArbSet(arbs).filter((issue) => issue.level === "error");
  if (issues.length) throw new Error(issues[0]?.message);
  const directoryHandle = {} as FileSystemDirectoryHandle;
  loadWorkspace({ directoryHandle, arbs, metadata: extractMetadataFromArb(arbs.en) });
}

openButton.addEventListener("click", openFolder);
welcomeOpenButton.addEventListener("click", openFolder);
downloadButton.addEventListener("click", downloadArbs);
saveButton.addEventListener("click", saveChanges);
loadMoreButton.addEventListener("click", () => { displayLimit += 300; renderList(); });

keyListElement.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-key]");
  if (!button || !store) return;
  store.selectedKey = button.dataset.key ?? store.selectedKey;
  renderList();
  renderEditor();
  editorElement.scrollTo({ top: 0, behavior: "smooth" });
});

orphanListElement.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-remove-orphan]");
  const key = button?.dataset.removeOrphan;
  if (!key || !store || !window.confirm(`Remove orphaned metadata for “${key}”?`)) return;
  store.removeOrphanMetadata(key);
  renderSummary();
  renderList();
  setSaveState();
});

element<HTMLInputElement>("#search").addEventListener("input", (event) => {
  filters.search = (event.currentTarget as HTMLInputElement).value;
  displayLimit = 300;
  renderList();
});
element<HTMLSelectElement>("#locale-filter").addEventListener("change", (event) => {
  filters.locale = (event.currentTarget as HTMLSelectElement).value as FilterState["locale"];
  displayLimit = 300;
  renderList();
});
element<HTMLSelectElement>("#status-filter").addEventListener("change", (event) => {
  filters.status = (event.currentTarget as HTMLSelectElement).value as FilterState["status"];
  displayLimit = 300;
  renderList();
});
element<HTMLSelectElement>("#issue-filter").addEventListener("change", (event) => {
  filters.issue = (event.currentTarget as HTMLSelectElement).value as FilterState["issue"];
  displayLimit = 300;
  renderList();
});
element<HTMLSelectElement>("#tag-filter").addEventListener("change", (event) => {
  filters.tag = (event.currentTarget as HTMLSelectElement).value;
  displayLimit = 300;
  renderList();
});

document.addEventListener("keydown", (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === "k") {
    event.preventDefault();
    element<HTMLInputElement>("#search").focus();
  }
  if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === "s") {
    event.preventDefault();
    void saveChanges();
  }
});

window.addEventListener("beforeunload", (event) => {
  if (!store?.isDirty) return;
  event.preventDefault();
  event.returnValue = "";
});

const browserNote = element<HTMLElement>("#browser-note");
const folderSupportError = writableDirectorySupportError();
if (folderSupportError) {
  browserNote.textContent = folderSupportError;
} else {
  browserNote.textContent = "Chrome or Edge will ask once for permission to read and write the selected folder.";
}

if (import.meta.env.DEV && new URLSearchParams(location.search).get("demo") === "1") {
  void loadDemo().catch((error) => showToast(error instanceof Error ? error.message : String(error), "error"));
}
